import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { 
  Users, 
  Phone, 
  Mail, 
  DollarSign, 
  FileText,
  Clock,
  Building2,
  User,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  XCircle
} from 'lucide-react';

interface OperadoresDetalhesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCompany?: string;
}

interface Contato {
  id: string;
  tipo: string;
  descricao: string;
  createdAt: string;
  cliente: {
    id: string;
    nome: string;
    codigo: string;
    company: {
      id: string;
      name: string;
    };
  };
  titulo?: {
    id: string;
    numero: string;
    valor: number;
    vencimento: string;
    status: string;
  };
  valorNegociado?: number;
  proximoContato?: string;
}

interface Operador {
  id: string;
  nome: string;
  email: string;
  totalContatos: number;
  clientesUnicos: number;
  empresasUnicas: number;
  contatos: Contato[];
}

const getTipoIcon = (tipo: string) => {
  switch (tipo.toLowerCase()) {
    case 'contato':
      return <Phone className="w-4 h-4 text-blue-500" />;
    case 'email':
      return <Mail className="w-4 h-4 text-green-500" />;
    case 'whatsapp':
      return <MessageSquare className="w-4 h-4 text-green-600" />;
    case 'negociacao':
      return <DollarSign className="w-4 h-4 text-yellow-500" />;
    case 'pagamento':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    default:
      return <FileText className="w-4 h-4 text-gray-500" />;
  }
};

const getTipoColor = (tipo: string) => {
  switch (tipo.toLowerCase()) {
    case 'contato':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'email':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-500/20 dark:text-green-200 dark:border-green-800 dark:bg-green-500/20 dark:text-green-200 dark:border-green-800';
    case 'whatsapp':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-500/20 dark:text-green-200 dark:border-green-800 dark:bg-green-500/20 dark:text-green-200 dark:border-green-800';
    case 'negociacao':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'pagamento':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-500/20 dark:text-green-200 dark:border-green-800 dark:bg-green-500/20 dark:text-green-200 dark:border-green-800';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'ABERTO':
      return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    case 'VENCIDO':
      return <XCircle className="w-4 h-4 text-red-500" />;
    case 'FECHADO':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'PARCIAL':
      return <Clock className="w-4 h-4 text-orange-500" />;
    default:
      return <FileText className="w-4 h-4 text-gray-500" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'ABERTO':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'VENCIDO':
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-500/20 dark:text-red-200 dark:border-red-800 dark:bg-red-500/20 dark:text-red-200 dark:border-red-800';
    case 'FECHADO':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-500/20 dark:text-green-200 dark:border-green-800 dark:bg-green-500/20 dark:text-green-200 dark:border-green-800';
    case 'PARCIAL':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const OperadoresDetalhesModal: React.FC<OperadoresDetalhesModalProps> = ({
  isOpen,
  onClose,
  selectedCompany
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState('30d');

  // Buscar dados dos operadores
  const { data: operadoresData, isLoading, error } = useQuery({
    queryKey: ['operadores-detalhes', selectedCompany, selectedPeriod],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCompany) {
        params.append('companyId', selectedCompany);
      }
      params.append('period', selectedPeriod);
      
      const response = await api.get(`/reports/operadores-detalhes?${params.toString()}`);
      return response.data.data;
    },
    enabled: isOpen,
    refetchInterval: 30000, // Atualizar a cada 30 segundos
    refetchIntervalInBackground: true, // Continuar atualizando mesmo com aba inativa
  });

  const operadores: Operador[] = operadoresData?.operadores || [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Detalhamento por Operador"
      size="xl"
    >
      <div className="space-y-6">
        {/* Filtros */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium">Período:</label>
            <Select
              value={selectedPeriod}
              onValueChange={setSelectedPeriod}
              className="w-32"
            >
              <option value="7d">7 dias</option>
              <option value="30d">30 dias</option>
              <option value="90d">90 dias</option>
            </Select>
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Carregando dados...</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-8">
            <p className="text-red-500">Erro ao carregar dados dos operadores</p>
          </div>
        )}

        {/* Lista de Operadores */}
        {!isLoading && !error && operadores.length > 0 && (
          <div className="space-y-4">
            {operadores.map((operador) => (
              <Card key={operador.id} className="border border-border">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{operador.nome}</CardTitle>
                        <p className="text-sm text-muted-foreground">{operador.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">{operador.totalContatos}</div>
                      <p className="text-xs text-gray-500">Total de Contatos</p>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {/* Estatísticas do Operador */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-lg font-semibold text-green-600">{operador.clientesUnicos}</div>
                      <p className="text-xs text-gray-500">Clientes Únicos</p>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-purple-600">{operador.empresasUnicas}</div>
                      <p className="text-xs text-gray-500">Empresas</p>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-orange-600">
                        {operador.totalContatos > 0 ? Math.round(operador.totalContatos / operador.clientesUnicos * 10) / 10 : 0}
                      </div>
                      <p className="text-xs text-gray-500">Contatos/Cliente</p>
                    </div>
                  </div>

                  {/* Lista de Contatos */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-sm text-gray-700 dark:text-slate-300">
                      Contatos Realizados ({operador.contatos.length})
                    </h4>
                    
                    {operador.contatos.slice(0, 10).map((contato) => (
                      <div key={contato.id} className="border border-gray-100 dark:border-slate-700 dark:border-slate-700 dark:border-slate-600 rounded-lg p-3">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            {getTipoIcon(contato.tipo)}
                            <Badge className={getTipoColor(contato.tipo)}>
                              {contato.tipo}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {formatDate(contato.createdAt)}
                            </span>
                          </div>
                          {contato.valorNegociado && (
                            <div className="text-sm font-medium text-green-600">
                              {formatCurrency(contato.valorNegociado)}
                            </div>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-700 dark:text-slate-300 mb-2">
                          {contato.descricao}
                        </p>
                        
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1">
                              <Building2 className="w-3 h-3" />
                              <span>{contato.cliente.company.name}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <User className="w-3 h-3" />
                              <span>{contato.cliente.nome}</span>
                            </div>
                          </div>
                          
                          {contato.titulo && (
                            <div className="flex items-center space-x-2">
                              <Badge className={getStatusColor(contato.titulo.status)}>
                                {getStatusIcon(contato.titulo.status)}
                                <span className="ml-1">{contato.titulo.status}</span>
                              </Badge>
                              <span className="text-xs">
                                {contato.titulo.numero} - {formatCurrency(contato.titulo.valor)}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {contato.proximoContato && (
                          <div className="mt-2 flex items-center space-x-1 text-xs text-blue-600">
                            <Clock className="w-3 h-3" />
                            <span>Próximo contato: {formatDate(contato.proximoContato)}</span>
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {operador.contatos.length > 10 && (
                      <div className="text-center text-sm text-gray-500">
                        ... e mais {operador.contatos.length - 10} contatos
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Nenhum operador encontrado */}
        {!isLoading && !error && operadores.length === 0 && (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nenhum operador encontrado para o período selecionado</p>
          </div>
        )}
      </div>
    </Modal>
  );
};
